import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchTestOrders,
  fetchTestOrderResults,
  updateTestOrderPaymentStatus,
  updateTestOrderStatus,
} from "../../store/slices/testOrderSlice";
import { fetchTests } from "../../store/slices/testSlice";
import toast from "../../utils/toast";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Copy,
  Loader2,
  RefreshCw,
  Search,
  X,
} from "lucide-react";

const TestOrdersList = () => {
  const dispatch = useDispatch();
  const { testOrders, testOrderResults, loading, error } = useSelector(
    (s) => s.testOrders
  );
  const { tests } = useSelector((s) => s.tests || { tests: [] });

  const [statusFilter, setStatusFilter] = useState("all");
  const [dateMode, setDateMode] = useState("today");
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [expandedVisitIds, setExpandedVisitIds] = useState({});
  const [selectedPrintOrderIds, setSelectedPrintOrderIds] = useState([]);
  const [updatingPaymentGroupKey, setUpdatingPaymentGroupKey] = useState(null);

  useEffect(() => {
    dispatch(fetchTestOrders({}));
  }, [dispatch]);

  useEffect(() => {
    const onSync = () => dispatch(fetchTestOrders({}));
    window.addEventListener("lis:sync-completed", onSync);
    return () => window.removeEventListener("lis:sync-completed", onSync);
  }, [dispatch]);

  useEffect(() => {
    if (!Array.isArray(tests) || tests.length === 0) {
      dispatch(fetchTests());
    }
  }, [dispatch, tests]);

  const todayYmd = useMemo(() => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  }, []);

  // Normalize payloads to arrays
  const ordersList = useMemo(() => {
    if (Array.isArray(testOrders)) return testOrders;
    return testOrders?.data || [];
  }, [testOrders]);

  const testsById = useMemo(() => {
    const list = Array.isArray(tests) ? tests : tests?.data || [];
    return Object.fromEntries(list.map((t) => [t.id, t]));
  }, [tests]);

  const normalizeStatus = useCallback((s) => (s ? String(s) : ""), []);

  const summarizeGroupStatus = useCallback((orders) => {
    const statuses = orders.map((o) => normalizeStatus(o.status));
    if (statuses.some((s) => s === "in_progress")) return "in_progress";
    if (statuses.some((s) => s === "sample_collected")) return "sample_collected";
    if (statuses.length > 0 && statuses.every((s) => s === "completed")) return "completed";
    if (statuses.some((s) => s === "ordered")) return "ordered";
    if (statuses.some((s) => s === "cancelled")) return "cancelled";
    return statuses.find(Boolean) || "unknown";
  }, [normalizeStatus]);

  const summarizeGroupPriority = useCallback((orders) => {
    if (orders.some((o) => String(o.priority || "").toLowerCase() === "urgent")) return "urgent";
    return "normal";
  }, []);

  const summarizeGroupPaymentStatus = useCallback((orders) => {
    const statuses = Array.from(
      new Set((orders || []).map((o) => String(o.payment_status || "unpaid").toLowerCase()))
    );
    if (statuses.length === 1) return statuses[0];
    return "mixed";
  }, []);

  const groups = useMemo(() => {
    const list = Array.isArray(ordersList) ? ordersList : [];
    const map = new Map();

    const toYmd = (value) => {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      const pad = (n) => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    for (const o of list) {
      const visitId = o.visit_id ?? o.visitId;
      const key = visitId == null ? `order-${o.id}` : `visit-${visitId}`;
      const existing = map.get(key);

      const patientName = o.patient_name || o.full_name || `Patient #${o.patient_id || "-"}`;
      const cardNumber = o.card_number || "";
      const visitDate = o.visit_date || toYmd(o.ordered_at) || null;
      const visitTime = o.visit_time || null;

      if (!existing) {
        map.set(key, {
          key,
          visit_id: visitId ?? null,
          visit_date: visitDate,
          visit_time: visitTime,
          patient_name: patientName,
          card_number: cardNumber,
          orders: [o],
        });
      } else {
        existing.orders.push(o);
        // Prefer first non-empty values
        if (!existing.visit_date && visitDate) existing.visit_date = visitDate;
        if (!existing.visit_time && visitTime) existing.visit_time = visitTime;
        if (!existing.patient_name && patientName) existing.patient_name = patientName;
        if (!existing.card_number && cardNumber) existing.card_number = cardNumber;
      }
    }

    const out = Array.from(map.values());
    for (const g of out) {
      g.status = summarizeGroupStatus(g.orders);
      g.priority = summarizeGroupPriority(g.orders);
      g.payment_status = summarizeGroupPaymentStatus(g.orders);
      g.test_names = g.orders.map((o) => testsById[o.test_id]?.name || `Test #${o.test_id}`);
      g.tracking_code = g.visit_id ? `WB-${g.visit_id}` : `WB-${g.orders?.[0]?.id}`;
    }
    out.sort((a, b) => {
      const ad = a.visit_date || "";
      const bd = b.visit_date || "";
      if (ad !== bd) return bd.localeCompare(ad);
      const at = a.visit_time || "";
      const bt = b.visit_time || "";
      return bt.localeCompare(at);
    });
    return out;
  }, [ordersList, testsById, summarizeGroupPaymentStatus, summarizeGroupPriority, summarizeGroupStatus]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return groups.filter((g) => {
      const statusOk = statusFilter === "all" || g.status === statusFilter;
      const dateOk =
        dateMode === "all" ? true : (g.visit_date ? String(g.visit_date) === todayYmd : false);
      if (!statusOk || !dateOk) return false;
      if (!s) return true;

      const name = (g.patient_name || "").toLowerCase();
      const card = (g.card_number || "").toLowerCase();
      const visit = String(g.visit_id || "");
      const tracking = String(g.tracking_code || "").toLowerCase();
      const tests = (g.test_names || []).join(" ").toLowerCase();

      return (
        name.includes(s) ||
        card.includes(s) ||
        visit.includes(s) ||
        tracking.includes(s) ||
        tests.includes(s)
      );
    });
  }, [groups, statusFilter, dateMode, todayYmd, search]);

  const getStatusBadge = (status) => {
    const map = {
      ordered: "bg-blue-100 text-blue-800",
      sample_collected: "bg-purple-100 text-purple-800",
      in_progress: "bg-orange-100 text-orange-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-gray-100 text-gray-700",
    };
    return `px-2 py-1 text-xs rounded-full ${
      map[status] || "bg-gray-100 text-gray-700"
    }`;
  };

  const getPriorityBadge = (priority) => {
    const map = {
      normal: "bg-gray-100 text-gray-800",
      urgent: "bg-red-100 text-red-700",
    };
    return `px-2 py-1 text-xs rounded-full ${
      map[priority] || "bg-gray-100 text-gray-800"
    }`;
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const map = {
      unpaid: "bg-rose-100 text-rose-800",
      partial: "bg-amber-100 text-amber-800",
      paid: "bg-emerald-100 text-emerald-800",
      refunded: "bg-slate-100 text-slate-700",
    };
    const normalized = String(paymentStatus || "unpaid").toLowerCase();
    return `px-2 py-1 text-xs rounded-full ${
      map[normalized] || "bg-gray-100 text-gray-700"
    }`;
  };

  const canEditGroup = (g) => {
    const statuses = (g.orders || []).map((o) => normalizeStatus(o.status));
    if (statuses.some((s) => s === "in_progress" || s === "completed")) return false;
    return true;
  };

  const toggleExpanded = (visitKey) => {
    setExpandedVisitIds((prev) => ({ ...prev, [visitKey]: !prev[visitKey] }));
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(String(text));
      toast.success("Copied");
    } catch {
      toast.info(String(text));
    }
  };

  const cancelTestInGroup = async (order) => {
    try {
      await dispatch(updateTestOrderStatus({ id: order.id, status: "cancelled" })).unwrap();
      toast.success("Test removed from order");
      dispatch(fetchTestOrders({}));
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to update order");
    }
  };

  const changePaymentStatusForGroup = async (group, paymentStatus) => {
    const groupOrders = Array.isArray(group?.orders) ? group.orders : [];
    if (!groupOrders.length) return;

    try {
      setUpdatingPaymentGroupKey(group.key);
      await Promise.all(
        groupOrders.map((order) =>
          dispatch(
            updateTestOrderPaymentStatus({ id: order.id, payment_status: paymentStatus })
          ).unwrap()
        )
      );
      toast.success(
        groupOrders.length > 1 ? "Payment status updated for the whole order" : "Payment status updated"
      );
      dispatch(fetchTestOrders({}));
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to update payment status");
    } finally {
      setUpdatingPaymentGroupKey(null);
    }
  };

  const togglePrintOrderSelection = (order) => {
    const id = order.id;
    const isCompleted = normalizeStatus(order.status) === "completed";
    if (!isCompleted) return;

    setSelectedPrintOrderIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((x) => x !== id);
      }
      return [...prev, id];
    });
  };

  const openResults = async (order) => {
    try {
      setSelectedOrder(order);
      await dispatch(fetchTestOrderResults(order.id)).unwrap();
      setShowResults(true);
    } catch (e) {
      toast.error(typeof e === "string" ? e : "Failed to fetch results");
    }
  };
  const openPrint = (order) => {
    // Open new window for printing
    const printWindow = window.open(
      `/print-report?orderId=${order.id}`,
      "_blank",
      "width=1200,height=800,scrollbars=yes"
    );

    setTimeout(() => {
      if (printWindow) {
        printWindow.focus();
        // The print will be triggered from the new window component
      }
    }, 1000);
  };

  const openPrintSelected = (orderIds) => {
    const ids = Array.isArray(orderIds) ? orderIds.filter(Boolean) : [];
    if (!ids.length) {
      toast.error("Select at least one completed test to print");
      return;
    }

    const url = `/print-report?orderIds=${ids.join(",")}`;
    const printWindow = window.open(
      url,
      "_blank",
      "width=1200,height=800,scrollbars=yes"
    );

    setTimeout(() => {
      if (printWindow) {
        printWindow.focus();
      }
    }, 1000);
  };
  const closeResults = () => {
    setSelectedOrder(null);
    setShowResults(false);
  };

  const resultsList = useMemo(() => {
    if (Array.isArray(testOrderResults)) return testOrderResults;
    return testOrderResults?.data || [];
  }, [testOrderResults]);

  return (
    <div className="pt-6">
      <div className="mx-auto max-w-7xl">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="border-t-4 border-[#36F1A2] bg-gradient-to-r from-gray-50 to-white px-5 py-4 sm:px-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <h1 className="text-xl font-semibold text-gray-900">Test orders</h1>
                <p className="mt-1 text-sm text-gray-600">
                  Orders are grouped by visit (one order can include multiple tests).
                </p>
              </div>
              <button
                onClick={() => dispatch(fetchTestOrders({}))}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                {loading ? "Refreshing…" : "Refresh"}
              </button>
            </div>
          </div>

          {error ? (
            <div className="px-5 pt-5 sm:px-6">
              <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 ring-1 ring-red-200">
                {String(error)}
              </div>
            </div>
          ) : null}

          <div className="px-5 py-5 sm:px-6">
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search patient, card, visit, tracking code, or test…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-xl bg-white py-2.5 pl-9 pr-9 text-sm text-gray-900 ring-1 ring-gray-200 shadow-sm transition placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    aria-label="Clear search"
                  >
                    <X className="h-4 w-4" />
                  </button>
                ) : null}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setDateMode((p) => (p === "today" ? "all" : "today"))}
                  className={
                    dateMode === "today"
                      ? "inline-flex items-center gap-2 rounded-full bg-[#235F72] px-3 py-2 text-xs font-semibold text-white"
                      : "inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200"
                  }
                >
                  <Calendar className="h-4 w-4" />
                  {dateMode === "today" ? "Today" : "All dates"}
                </button>

                {[
                  "all",
                  "ordered",
                  "sample_collected",
                  "in_progress",
                  "completed",
                  "cancelled",
                ].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setStatusFilter(s)}
                    className={
                      statusFilter === s
                        ? "rounded-full bg-[#235F72] px-3 py-2 text-xs font-semibold text-white"
                        : "rounded-full bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200"
                    }
                  >
                    {s.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-5 rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200/70">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-10 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading orders…
                </div>
              ) : filtered.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-600">
                  <div>No orders found.</div>
                  {dateMode === "today" && groups.length > 0 ? (
                    <button
                      type="button"
                      onClick={() => setDateMode("all")}
                      className="mt-3 inline-flex items-center justify-center rounded-xl bg-white px-4 py-2 text-xs font-semibold text-[#235F72] ring-1 ring-gray-200 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                    >
                      Show all dates
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="space-y-3">
                  {filtered.map((g) => {
                    const expanded = !!expandedVisitIds[g.key];
                    const orderedAt = g.visit_date
                      ? `${g.visit_date}${g.visit_time ? ` • ${g.visit_time}` : ""}`
                      : "-";

                    return (
                      <div key={g.key} className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-gray-200">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <button
                                type="button"
                                onClick={() => toggleExpanded(g.key)}
                                className="inline-flex items-center gap-2 rounded-xl bg-gray-50 px-3 py-1.5 text-sm font-semibold text-gray-900 ring-1 ring-gray-200 hover:bg-gray-100"
                              >
                                {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                {g.visit_id ? `Visit #${g.visit_id}` : "Order"}
                              </button>

                              <span className={getStatusBadge(g.status)}>{(g.status || "").replace(/_/g, " ")}</span>
                              <span className={getPriorityBadge(g.priority)}>{g.priority}</span>
                            </div>

                            <div className="mt-2 min-w-0">
                              <div className="truncate text-sm font-semibold text-gray-900">{g.patient_name}</div>
                              <div className="mt-0.5 text-xs text-gray-600">
                                {g.card_number ? `Card: ${g.card_number} • ` : ""}Ordered: {orderedAt}
                              </div>
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-2">
                              <div className="text-xs font-semibold text-gray-600">Tracking:</div>
                              <div className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-gray-200">
                                {g.tracking_code}
                              </div>
                              <button
                                type="button"
                                onClick={() => copyText(g.tracking_code)}
                                className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200 hover:bg-gray-200"
                              >
                                <Copy className="h-3.5 w-3.5" />
                                Copy
                              </button>
                            </div>

                            <div className="mt-3 flex flex-wrap gap-2">
                              {(g.test_names || []).slice(0, 6).map((t, idx) => (
                                <span
                                  key={`${g.key}-t-${idx}`}
                                  className="rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-[#085DB6] ring-1 ring-blue-200"
                                >
                                  {t}
                                </span>
                              ))}
                              {g.test_names && g.test_names.length > 6 ? (
                                <span className="rounded-full bg-gray-100 px-3 py-1 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                                  +{g.test_names.length - 6} more
                                </span>
                              ) : null}
                            </div>
                          </div>

                          <div className="flex flex-wrap items-center gap-2">
                            <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2 ring-1 ring-gray-200">
                              <span className={getPaymentStatusBadge(g.payment_status)}>
                                {String(g.payment_status || "unpaid").replace(/_/g, " ")}
                              </span>
                              <select
                                value={g.payment_status === "mixed" ? "" : g.payment_status}
                                onChange={(e) => changePaymentStatusForGroup(g, e.target.value)}
                                disabled={updatingPaymentGroupKey === g.key}
                                className="rounded-lg border border-gray-200 bg-white px-2 py-1 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                              >
                                <option value="" disabled>
                                  {g.payment_status === "mixed" ? "Mixed payment" : "Update payment"}
                                </option>
                                <option value="unpaid">Unpaid</option>
                                <option value="partial">Partial</option>
                                <option value="paid">Paid</option>
                                <option value="refunded">Refunded</option>
                              </select>
                            </div>
                            <button
                              type="button"
                              onClick={() => toggleExpanded(g.key)}
                              className="rounded-xl bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-200 shadow-sm hover:bg-gray-50"
                            >
                              {expanded ? "Hide" : "Details"}
                            </button>
                            {canEditGroup(g) ? (
                              <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-800 ring-1 ring-amber-200">
                                Editable
                              </div>
                            ) : (
                              <div className="rounded-xl bg-gray-100 px-3 py-2 text-xs font-semibold text-gray-700 ring-1 ring-gray-200">
                                Locked
                              </div>
                            )}
                          </div>
                        </div>

                        {expanded ? (
                          <div className="mt-4 rounded-2xl bg-gray-50 p-3 ring-1 ring-gray-200">
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-600">
                                    <th className="py-2 pr-4 w-10">Select</th>
                                    <th className="py-2 pr-4">Test</th>
                                    <th className="py-2 pr-4">Status</th>
                                    <th className="py-2 pr-4">Payment</th>
                                    <th className="py-2 pr-4">Priority</th>
                                    <th className="py-2 pr-4">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {(g.orders || []).map((o) => {
                                    const testName = testsById[o.test_id]?.name || `Test #${o.test_id}`;
                                    const locked = ["in_progress", "completed"].includes(normalizeStatus(o.status));
                                    const isCompleted = normalizeStatus(o.status) === "completed";
                                    const isSelectedForPrint = selectedPrintOrderIds.includes(o.id);
                                    return (
                                      <tr key={o.id} className="border-b border-gray-200 last:border-b-0">
                                        <td className="py-3 pr-2 align-middle">
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-[#085DB6] focus:ring-[#085DB6]"
                                            disabled={!isCompleted}
                                            checked={isSelectedForPrint}
                                            onChange={() => togglePrintOrderSelection(o)}
                                          />
                                        </td>
                                        <td className="py-3 pr-4">
                                          <div className="text-sm font-semibold text-gray-900">{testName}</div>
                                          <div className="mt-0.5 text-xs text-gray-600">Order #{o.id}</div>
                                        </td>
                                        <td className="py-3 pr-4">
                                          <span className={getStatusBadge(o.status)}>
                                            {(o.status || "unknown").replace(/_/g, " ")}
                                          </span>
                                        </td>
                                        <td className="py-3 pr-4">
                                          <span className={getPaymentStatusBadge(o.payment_status)}>
                                            {String(o.payment_status || "unpaid").replace(/_/g, " ")}
                                          </span>
                                        </td>
                                        <td className="py-3 pr-4">
                                          <span className={getPriorityBadge(o.priority)}>
                                            {o.priority || "normal"}
                                          </span>
                                        </td>
                                        <td className="py-3 pr-4">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <button
                                              type="button"
                                              disabled={normalizeStatus(o.status) !== "completed"}
                                              onClick={() => openResults(o)}
                                              className={
                                                normalizeStatus(o.status) === "completed"
                                                  ? "rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#085DB6] ring-1 ring-gray-200 hover:bg-gray-50"
                                                  : "cursor-not-allowed rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-400 ring-1 ring-gray-200"
                                              }
                                            >
                                              View Results
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => openPrint(o)}
                                              className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-[#085DB6] ring-1 ring-gray-200 hover:bg-gray-50"
                                            >
                                              Print
                                            </button>
                                            <button
                                              type="button"
                                              disabled={locked}
                                              onClick={() => cancelTestInGroup(o)}
                                              className={
                                                locked
                                                  ? "cursor-not-allowed rounded-lg bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-400 ring-1 ring-gray-200"
                                                  : "rounded-lg bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-700 ring-1 ring-red-200 hover:bg-red-100"
                                              }
                                            >
                                              Remove
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {(() => {
                              const groupOrderIds = (g.orders || []).map((o) => o.id);
                              const selectedIdsInGroup = selectedPrintOrderIds.filter((id) => groupOrderIds.includes(id));
                              if (!selectedIdsInGroup.length) return null;

                              return (
                                <div className="mt-3 flex items-center justify-between">
                                  <div className="text-xs text-gray-600">
                                    {selectedIdsInGroup.length} completed test(s) selected for printing
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => openPrintSelected(selectedIdsInGroup)}
                                    className="inline-flex items-center justify-center rounded-xl bg-white px-3 py-1.5 text-xs font-semibold text-[#085DB6] ring-1 ring-gray-200 shadow-sm hover:bg-gray-50"
                                  >
                                    Print selected
                                  </button>
                                </div>
                              );
                            })()}

                            {!canEditGroup(g) ? (
                              <div className="mt-3 text-xs text-gray-600">
                                Editing is locked once processing has started.
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

      {/* (table removed in favor of grouped cards) */}

      {/* Results Modal */}
      {showResults && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-6">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-xl ring-1 ring-gray-200">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-gray-900">Test results</div>
                <div className="mt-0.5 text-xs text-gray-600">Order #{selectedOrder?.id}</div>
              </div>
              <button
                type="button"
                onClick={closeResults}
                className="inline-flex items-center justify-center rounded-xl bg-white p-2 text-gray-600 ring-1 ring-gray-200 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="max-h-[70vh] space-y-3 overflow-y-auto px-5 py-4">
              {loading ? (
                <div className="flex items-center justify-center gap-2 py-12 text-sm text-gray-600">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading results…
                </div>
              ) : resultsList.length === 0 ? (
                <div className="rounded-2xl bg-gray-50 px-4 py-12 text-center text-sm text-gray-600 ring-1 ring-gray-200/70">
                  No results available.
                </div>
              ) : (
                resultsList.map((r) => (
                  <div key={r.id} className="rounded-2xl bg-white p-4 ring-1 ring-gray-200">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm text-gray-700">
                          Value:{" "}
                          <span className="font-semibold text-gray-900">
                            {r.result_value || r.numeric_value || "-"}
                          </span>{" "}
                          {r.unit ? <span className="text-gray-500">{r.unit}</span> : null}
                        </div>
                        {r.comments ? (
                          <div className="mt-1 text-xs text-gray-600">Comments: {r.comments}</div>
                        ) : null}
                        {r.interpretation ? (
                          <div className="mt-1 text-xs text-gray-600">Interpretation: {r.interpretation}</div>
                        ) : null}
                        <div className="mt-2 text-xs text-gray-500">
                          Verified: {r.is_verified ? "Yes" : "No"}
                          {r.verified_at ? ` (${new Date(r.verified_at).toLocaleString()})` : ""}
                        </div>
                      </div>

                      <span
                        className={
                          "shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 " +
                          (r.status === "high"
                            ? "bg-red-50 text-red-700 ring-red-200"
                            : r.status === "low"
                            ? "bg-yellow-50 text-yellow-800 ring-yellow-200"
                            : r.status === "critical"
                            ? "bg-purple-50 text-purple-800 ring-purple-200"
                            : "bg-green-50 text-green-700 ring-green-200")
                        }
                      >
                        {r.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={closeResults}
                className="inline-flex items-center justify-center rounded-xl bg-[#235F72] px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-95 focus:outline-none focus:ring-2 focus:ring-[#36F1A2]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default TestOrdersList;
